import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { Skeleton } from '../../../components/ui/Skeleton';
import {
  useLoans,
  useApproveLoan,
  useRejectLoan,
  useDisburseLoan,
} from '../../../hooks/useCredit';
import type { Loan, LoanStatus, CreditProductType } from '../../../types/credit';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('pt-BR');
};

const statusLabels: Record<LoanStatus, string> = {
  PENDING_ANALYSIS: 'Análise Pendente',
  PENDING_APPROVAL: 'Aprovação Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  PENDING_SIGNATURE: 'Aguardando Assinatura',
  PENDING_DISBURSEMENT: 'Aguardando Desembolso',
  ACTIVE: 'Ativo',
  PAID_OFF: 'Quitado',
  DEFAULTED: 'Inadimplente',
  RENEGOTIATED: 'Renegociado',
  CANCELLED: 'Cancelado',
};

const statusColors: Record<LoanStatus, 'default' | 'primary' | 'success' | 'error'> = {
  PENDING_ANALYSIS: 'default',
  PENDING_APPROVAL: 'primary',
  APPROVED: 'success',
  REJECTED: 'error',
  PENDING_SIGNATURE: 'primary',
  PENDING_DISBURSEMENT: 'primary',
  ACTIVE: 'success',
  PAID_OFF: 'success',
  DEFAULTED: 'error',
  RENEGOTIATED: 'default',
  CANCELLED: 'default',
};

const productTypeLabels: Record<CreditProductType, string> = {
  PERSONAL_LOAN: 'Pessoal',
  PAYROLL_LOAN: 'Consignado',
  RECEIVABLES_ADVANCE: 'Antecipação',
  WORKING_CAPITAL: 'Capital de Giro',
};

export function LoanRequests() {
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const { data: loansData, isLoading } = useLoans({
    status: 'PENDING_APPROVAL',
  });
  const approveLoan = useApproveLoan();
  const rejectLoan = useRejectLoan();
  const disburseLoan = useDisburseLoan();

  const handleApprove = async (loan: Loan) => {
    try {
      await approveLoan.mutateAsync({ loanId: loan.id });
    } catch (error) {
      console.error('Erro ao aprovar:', error);
    }
  };

  const handleReject = async () => {
    if (!selectedLoan || !rejectReason) return;
    
    try {
      await rejectLoan.mutateAsync({
        loanId: selectedLoan.id,
        reason: rejectReason,
      });
      setIsRejectModalOpen(false);
      setRejectReason('');
      setSelectedLoan(null);
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
    }
  };

  const handleDisburse = async (loan: Loan) => {
    try {
      await disburseLoan.mutateAsync(loan.id);
    } catch (error) {
      console.error('Erro ao desembolsar:', error);
    }
  };

  const openRejectModal = (loan: Loan) => {
    setSelectedLoan(loan);
    setIsRejectModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  const loans = loansData?.loans || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Solicitações de Empréstimo</h1>
        <Badge variant="primary">{loans.length} pendentes</Badge>
      </div>

      {loans.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            Nenhuma solicitação pendente de aprovação.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {loans.map((loan) => (
            <Card key={loan.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">
                      {formatCurrency(loan.requestedAmount)}
                    </CardTitle>
                    <p className="text-sm text-gray-500">
                      {loan.installmentCount}x de{' '}
                      {formatCurrency(loan.requestedAmount / loan.installmentCount)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant={statusColors[loan.status]}>
                      {statusLabels[loan.status]}
                    </Badge>
                    <Badge variant="default">
                      {productTypeLabels[loan.productType]}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Score</p>
                    <p className="font-medium">{loan.scoreAtRequest}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">CET</p>
                    <p className="font-medium">{loan.cet.toFixed(2)}% a.a.</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Taxa Mensal</p>
                    <p className="font-medium">
                      {(loan.monthlyInterestRate * 100).toFixed(2)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Solicitado em</p>
                    <p className="font-medium">{formatDate(loan.requestedAt)}</p>
                  </div>
                </div>

                {loan.analysisResult && (
                  <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <p className="text-sm font-medium mb-2">Análise de Crédito</p>
                    <div className="text-sm text-gray-600">
                      <p>
                        Score Interno: {(loan.analysisResult as any).score?.internal || 'N/A'}
                      </p>
                      <p>
                        Score Externo: {(loan.analysisResult as any).score?.external || 'N/A'}
                      </p>
                      <p>
                        Score Combinado: {(loan.analysisResult as any).score?.combined || 'N/A'}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  {loan.status === 'PENDING_APPROVAL' && (
                    <>
                      <Button
                        onClick={() => handleApprove(loan)}
                        disabled={approveLoan.isPending}
                      >
                        Aprovar
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => openRejectModal(loan)}
                      >
                        Rejeitar
                      </Button>
                    </>
                  )}
                  {loan.status === 'APPROVED' && (
                    <Button
                      onClick={() => handleDisburse(loan)}
                      disabled={disburseLoan.isPending}
                    >
                      Desembolsar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        title="Rejeitar Solicitação"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Informe o motivo da rejeição para o cliente.
          </p>
          <Input
            label="Motivo da Rejeição"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ex: Score insuficiente, renda incompatível..."
            required
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setIsRejectModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReject}
              disabled={!rejectReason || rejectLoan.isPending}
            >
              {rejectLoan.isPending ? 'Rejeitando...' : 'Confirmar Rejeição'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default LoanRequests;
